import {
  Box,
  Input,
  VStack,
  Text,
  Icon,
  Alert,
} from "@chakra-ui/react";
import React, { useState } from "react";
import useThemeColors from "../../hooks/useThemeColors";
import useIcons from "../../hooks/useIcons";
import CustomModal from "./CustomModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  title: string;
  description?: string;
  accept?: string;
  isUploading?: boolean;
}

const FileUploadModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onUpload,
  title,
  description = "Drag & drop or click to select Excel or CSV file",
  accept = ".xlsx, .xls, .csv",
  isUploading = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const colors = useThemeColors();
  const icons = useIcons();
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile);
    }
    setSelectedFile(null);
  };

  const handleModalClose = () => {
    setSelectedFile(null);
    onClose();
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        handleClose={handleModalClose}
        mode="add"
        title={title}
        actionText="Upload"
        icon={icons.utility.upload}
        handleAction={handleUpload}
        isLoading={isUploading}
        isDisabled={!selectedFile || isUploading}
        hasChanges={!!selectedFile}
        
      >
        <VStack gap={6} align="stretch">
          {/* File Upload Area */}
          <Box
            p={10}
            border="2px dashed"
            borderColor={colors.border.secondary}
            borderRadius="lg"
            textAlign="center"
            width="full"
            position="relative"
            _hover={{ borderColor: colors.brand.primary }}
            transition="border-color 0.2s"
          >
            <Input
              type="file"
              height="full"
              width="full"
              position="absolute"
              top="0"
              left="0"
              opacity="0"
              aria-hidden="true"
              accept={accept}
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <VStack>
              <Icon as={icons.utility.upload} boxSize={8} color="gray.500" />
              <Text fontWeight="medium">{description}</Text>
              <Text fontSize="sm" color="gray.500">
                Make sure your file matches the template format
              </Text>
            </VStack>
          </Box>

          {/* Selected File */}
          {selectedFile && (
            <Alert.Root status="info">
              <Icon
                as={icons.utility.excelFile}
                color="green.500"
                fontSize="2xl"
              />
              <Box ml={3}>
                <Text fontWeight="medium">
                  Selected file: {selectedFile.name}
                </Text>
                <Text fontSize="sm" opacity={0.6}>
                  Size: {(selectedFile.size / 1024).toFixed(1)} KB
                </Text>
              </Box>
            </Alert.Root>
          )}
        </VStack>
      </CustomModal>
    </>
  );
};

export default FileUploadModal;
